import { trackEvent } from '@lib/analytics'
import type { ItineraryImportSource } from '@orbit/api-types'

/** How many of an import's events are still on the trip: the ones the person kept. */
export const keptEventCount = (createdEventIds: number[], events: { id: number }[]): number => {
	const live = new Set(events.map(event => event.id))
	return createdEventIds.filter(id => live.has(id)).length
}

// The imports this browser has already recorded, newest last. The review can be
// reopened from the itinerary banner and left again, which must not count twice.
const RECORDED_KEY = 'itinerary.import.recorded'
const RECORDED_LIMIT = 20

const readRecorded = (): string[] => {
	try {
		const parsed: unknown = JSON.parse(localStorage.getItem(RECORDED_KEY) ?? '[]')
		return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
	} catch {
		return []
	}
}

const rememberRecorded = (importId: string, recorded: string[]): void => {
	try {
		localStorage.setItem(RECORDED_KEY, JSON.stringify([...recorded, importId].slice(-RECORDED_LIMIT)))
	} catch {
		// Private windows, blocked or full storage: a reopened review may count again.
	}
}

interface ImportKept {
	tripId: number
	importId: string
	source: ItineraryImportSource | undefined
	eventCount: number
}

/**
 * Leaving an import's review with events still on the trip commits it: posts
 * `itinerary_imported` with the number kept. The server never sees a commit,
 * so this is the one create event the client posts itself (ADR 0013). Removing
 * every event is a discard and posts nothing.
 */
export const recordImportKept = ({ tripId, importId, source, eventCount }: ImportKept): void => {
	if (eventCount === 0) return
	const recorded = readRecorded()
	if (recorded.includes(importId)) return
	trackEvent('itinerary_imported', { tripId, importId, ...(source && { source }), eventCount })
	rememberRecorded(importId, recorded)
}
